import React from 'react'

import { formatDistanceToNow } from 'date-fns'
import { observer } from 'mobx-react-lite'

import { ILoadedScript, useMst } from '#state'
import { SyncStatusLevel } from '#state/models/SyncStatus'

import { BadSocketCloud, SyncStatusCloud } from '../SyncStatusCloud'

import { showDebugSyncModal } from './DebugSyncModal'
import { useSyncStatusEffects } from './useSyncStatusEffects'

// exported for storybook
export const ScriptSyncStatusInternal = ({
  level,
  age,
  mostRecentSync,
  isSyncing,
}: {
  level: SyncStatusLevel
  age: number
  mostRecentSync: Date
  isSyncing: boolean
}) => {
  switch (level) {
    case 'ok': {
      return (
        <SyncStatusCloud
          status={level}
          tooltip={isSyncing ? 'Syncing' : 'Document in sync'}
          iconType={isSyncing ? 'syncing' : 'check'}
        />
      )
    }
    case 'warn': {
      const dots = ' . . .'.slice(0, (age % 4) * 2)
      return (
        <SyncStatusCloud
          status="warn"
          tooltip={'Trying to sync ' + dots}
          iconType="syncing"
        />
      )
    }
    case 'error':
    case 'fatal':
    default: {
      const ago = formatDistanceToNow(mostRecentSync, { addSuffix: true })
      return (
        <SyncStatusCloud
          status="error"
          tooltip={`Work last saved ${ago}`}
          iconType="xmark"
        />
      )
    }
  }
}

export const ScriptSyncStatus = observer(function ScriptSyncStatus({
  script,
}: {
  script: ILoadedScript
}) {
  const { socketManager, appStatus, view } = useMst()
  const { stalest, inSync, secondsOfStaleness, status } = script.syncStatus
  const { debugSync } = view

  const handleClick = () => {
    if (debugSync) {
      showDebugSyncModal({ script })
    }
  }

  useSyncStatusEffects(script)

  if (!appStatus.suspended && socketManager.status !== 'connected') {
    return <BadSocketCloud type={socketManager.status} />
  }

  return (
    <div onClick={handleClick}>
      <ScriptSyncStatusInternal
        level={status}
        age={secondsOfStaleness}
        mostRecentSync={stalest ?? new Date()}
        isSyncing={!inSync}
      />
    </div>
  )
})
